import React, { useCallback } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import { useFacebookCore } from "../../../../cores/facebook";
import { useInstagramPublishRouter } from "../../../../cores/instagram/useInstagramPublishRouter";
import { useLinkedInCore } from "../../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { useMeCore } from "../../../../cores/me";
import { useRouterCore } from "../../../../cores/router";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { useTwitterCore } from "../../../../cores/twitter";
import { updateSocialPostSuccessAction } from "../../../../cores/socialPost/store/api-actions";

interface Props {
  from: string;
  save?: boolean;
  disabled?: boolean;
  onReset: () => void;
  onSavedSuccess?: () => void;
  onDeletedSuccess?: () => void;
  /** Calendario: agenda un duplicado vía API (repostId) y refresca la grilla. */
  onDuplicateSchedule?: () => void;
  /** Calendario: tras guardar/publicar con éxito, recarga posts para reflejar imágenes/medios nuevos. */
  onPostsRefresh?: () => void;
}

export const PostActionButtons = ({
  from,
  save = true,
  disabled = false,
  onReset,
  onSavedSuccess,
  onDeletedSuccess,
  onDuplicateSchedule,
  onPostsRefresh,
}: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    modifySocialPost: modifySocialPostTwitter,
    modifySocialPostVideo: modifySocialPostVideoTwitter,
  } = useTwitterCore();
  const {
    modifySocialPost: modifySocialPostFacebook,
    modifySocialPostVideo: modifySocialPostVideoFacebook,
  } = useFacebookCore();
  const {
    modifySocialPost: modifySocialPostLinkedIn,
  } = useLinkedInCore();
  const {
    modifySocialPost: modifySocialPostLinkedInBusiness,
    modifySocialPostVideo: modifySocialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const {
    modifySocialPost: modifySocialPostInstagram,
    modifySocialPostVideo: modifySocialPostVideoInstagram,
  } = useInstagramPublishRouter();
  const { company } = useMeCore();
  const { goToSocialHistory, goToSocialCalendar } = useRouterCore();
  const {
    socialProviders,
    images,
    selectedPost,
    video,
    text = "",
    mentions,
    setSocialAction,
    clearSelectedPost,
    deleteSocialPost,
  } = useSocialPostCore();

  const handleModifySuccess = useCallback(
    (scheduleDate: string, actionType: string) => {
      if (selectedPost) {
        const nextPost = {
          ...selectedPost,
          post: {
            ...selectedPost.post,
            text,
            scheduleDate:
              actionType === SOCIAL_ACTION_TYPES.POST ? "" : scheduleDate,
            postAt:
              actionType === SOCIAL_ACTION_TYPES.POST
                ? moment().toString()
                : selectedPost.post.postAt,
          },
          mention: mentions,
        };
        dispatch(updateSocialPostSuccessAction({ post: nextPost }));
      }
      onReset();
      if (from === "calendar") {
        onPostsRefresh?.();
        onSavedSuccess?.();
      }
    },
    [dispatch, from, mentions, onPostsRefresh, onReset, onSavedSuccess, selectedPost, text]
  );

  const modify = useCallback((scheduleDate: string, actionType: string) => {
    setSocialAction(actionType);
    const onSuccess = () => handleModifySuccess(scheduleDate, actionType);

    selectedPost &&
      socialProviders.forEach((sp) => {
        switch (sp.id) {
          case SocialProviderId.Twitter:
            selectedPost.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0
              ? modifySocialPostVideoTwitter(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                )
              : modifySocialPostTwitter(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  images,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                );
            break;
          case SocialProviderId.LinkedInBusiness:
            selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0
              ? modifySocialPostVideoLinkedInBusiness(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                )
              : modifySocialPostLinkedInBusiness(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  images,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                );
            break;
          case SocialProviderId.LinkedIn:
            modifySocialPostLinkedIn(
              company?.id!,
              selectedPost.post.id,
              text,
              mentions,
              images,
              scheduleDate,
              selectedPost.media,
              onSuccess
            );
            break;
          case SocialProviderId.Facebook:
            selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0
              ? modifySocialPostVideoFacebook(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                )
              : modifySocialPostFacebook(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  images,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                );
            break;
          case SocialProviderId.Instagram:
            selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0
              ? modifySocialPostVideoInstagram(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                )
              : modifySocialPostInstagram(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  images,
                  scheduleDate,
                  selectedPost.media,
                  onSuccess
                );
            break;
          default:
            break;
        }
      });
  }, [
    selectedPost,
    socialProviders,
    company,
    text,
    video,
    images,
    mentions,
    modifySocialPostVideoTwitter,
    onReset,
    modifySocialPostTwitter,
    modifySocialPostVideoLinkedInBusiness,
    modifySocialPostLinkedInBusiness,
    modifySocialPostLinkedIn,
    modifySocialPostVideoFacebook,
    modifySocialPostFacebook,
    modifySocialPostVideoInstagram,
    modifySocialPostInstagram,
    setSocialAction,
    handleModifySuccess,
  ]);

  const saveEdition = useCallback(async () => {
    await modify(selectedPost?.post.scheduleDate || "", SOCIAL_ACTION_TYPES.SCHEDULE);
    if (from === "history") {
      goToSocialHistory();
    } else if (from !== "calendar") {
      goToSocialCalendar();
    }
    // Calendario: el modal se cierra en handleModifySuccess al confirmar el API
  }, [
    goToSocialHistory,
    goToSocialCalendar,
    modify,
    from,
    selectedPost?.post.scheduleDate,
  ]);

  const publishNow = useCallback(async () => {
    await modify(moment().toString(), SOCIAL_ACTION_TYPES.POST);
  }, [modify]);

  const duplicatePost = useCallback(() => {
    onDuplicateSchedule?.();
  }, [onDuplicateSchedule]);

  const deleteScheduledPost = useCallback(async () => {
    if (!selectedPost?.post?.id || !company?.id) return;
    if (selectedPost.post.postAt) return;
    const confirmed = window.confirm(
      "¿Seguro que quieres eliminar esta publicación programada?"
    );
    if (!confirmed) return;
    await deleteSocialPost(company.id, selectedPost.post.id);
    clearSelectedPost();
    onReset();
    if (from === "calendar") {
      onDeletedSuccess?.();
    }
  }, [
    selectedPost,
    company,
    deleteSocialPost,
    clearSelectedPost,
    onReset,
    from,
    onDeletedSuccess,
  ]);

  return (
    from === "calendar" && !save ? (
      <>
        <Button
          color="secondary"
          outline
          className="mx-2"
          disabled={disabled}
          onClick={duplicatePost}
        >
          Duplicar
        </Button>
        <Button
          color="primary"
          outline
          className="mx-2"
          disabled={disabled}
          onClick={saveEdition}
        >
          Guardar
        </Button>
        {!selectedPost?.post?.postAt && (
          <Button
            color="danger"
            outline
            className="mx-2"
            onClick={deleteScheduledPost}
          >
            Eliminar
          </Button>
        )}
        <Button color="primary" className="mx-2" disabled={disabled} onClick={publishNow}>
          Publicar ahora
        </Button>
      </>
    ) : save ? (
      <Button className="whiteButton mx-2" disabled={disabled} onClick={saveEdition}>
        {intl.formatMessage({ id: "app.socialMediaPost.save" })}
      </Button>
    ) : (
      <Button
        className="mr-3"
        color="primary"
        disabled={disabled}
        onClick={saveEdition}
      >
        {intl.formatMessage({ id: "app.socialMediaPost.toPost" })}
      </Button>
    )
  );
};
